import { useQuery } from '@tanstack/react-query';

import { catalogBanks } from '@/shared/api/resources';

import { Field } from './Field';
import { Select } from './Select';

/** O catalogo muda por migration, e nao durante o uso. */
const UMA_HORA = 60 * 60 * 1000;

export interface BankCodeSelectProps {
  label?: string | undefined;
  value: string;
  onChange: (code: string) => void;
  error?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  /**
   * Bancos ja carregados por quem usa o campo. O autocadastro publico (P-ACE-2) nao
   * tem sessao para ler o catalogo e recebe a lista junto do pedido; com ela, o campo
   * nao consulta o catalogo.
   */
  bancos?: { code: string; name: string }[] | undefined;
}

/**
 * Banco da conta, da relacao do Banco Central (P-TOM-C1, TOM-083).
 *
 * A lista e o catalogo da plataforma (`GET /api/v1/catalogs/bank-codes`), com o
 * codigo de compensacao e o nome, como o "Selecione o banco" do OnPoint. So banco
 * ativo recebe conta nova; o banco gravado que saiu da relacao continua aparecendo,
 * como foi gravado. Sem o catalogo, o campo volta a ser o codigo de tres digitos
 * digitado, e o servidor confere.
 */
export function BankCodeSelect({
  label = 'Banco',
  value,
  onChange,
  error,
  required,
  disabled,
  bancos: recebidos,
}: BankCodeSelectProps) {
  const bancos = useQuery({
    queryKey: ['catalogs', 'bank-codes'],
    queryFn: ({ signal }) => catalogBanks.list(signal),
    staleTime: UMA_HORA,
    enabled: recebidos === undefined,
  });

  if (recebidos !== undefined) {
    const opcoes = recebidos.map((banco) => ({
      value: banco.code,
      label: `${banco.code} - ${banco.name}`,
    }));
    if (value && !opcoes.some((opcao) => opcao.value === value)) {
      opcoes.push({ value, label: value });
    }
    return (
      <Select
        label={label}
        required={required}
        placeholder="Selecione o banco"
        options={opcoes}
        value={value}
        error={error}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (bancos.isError) {
    return (
      <Field
        label={label}
        required={required}
        inputMode="numeric"
        maxLength={3}
        placeholder="341"
        value={value}
        error={error}
        disabled={disabled}
        hint="A lista de bancos nao respondeu: digite o codigo de compensacao."
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  const ativos = (bancos.data ?? []).filter((banco) => banco.active);
  const opcoes = ativos.map((banco) => ({
    value: banco.compe_code,
    label: `${banco.compe_code} - ${banco.name}`,
  }));
  if (value && !opcoes.some((opcao) => opcao.value === value)) {
    const gravado = bancos.data?.find((banco) => banco.compe_code === value);
    opcoes.push({
      value,
      label: gravado
        ? `${value} - ${gravado.name} (fora da relacao)`
        : `${value} (como gravado)`,
    });
  }

  return (
    <Select
      label={label}
      required={required}
      placeholder={bancos.isPending ? 'Carregando os bancos' : 'Selecione o banco'}
      options={opcoes}
      value={value}
      error={error}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
